// Path: src/components/Layout/PublicLayout.tsx
// Integrated full layout with ChatPa, optimized z-index, and reduced useFirebase calls
// Enhanced SEO and mobile responsiveness
// 06/07/25 3:50 PM +07
import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import styled, { createGlobalStyle } from "styled-components";
import { Button, Tooltip } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import MenuLogin from "../auth/components/MenuLogin.tsx";
import OLSCalendar from "../auth/components/OLSCalendar";
import MiniLightTimeToggle from "../aols/aeo/OLSCalendarTime/MiniLightTimeToggle";
import ActivityLog from "../auth/ActivityLog";
import { Footer } from "../dev/utils/Footer";
import useOlsmeTimer from "../../hooks/useOlsmeTimer";
import ErrorBoundary from "../dev/utils/ErrorBoundary.tsx";
import TimerBar from "../TimerBar.tsx";
import ChatPa from "../chat/ChatPa.tsx";

const GlobalStyle = createGlobalStyle`
  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
  }
  #root {
    width: 100%;
    min-height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
  }
`;

const LayoutWrapper = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-size: 16px;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
`;

const MobileMenuButton = styled(Button)`
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 1010;
  background: #ffcc00;
  border: none;
  color: #0427f4;
  font-size: 18px;
  transition: background 0.3s ease, transform 0.3s ease;
  &:hover {
    background: #ffaa00;
    color: #0427f4;
    transform: scale(1.05);
  }
`;

const SiderContainer = styled.div<{ $collapsed: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  height: 100vh;
  width: ${(props) => (props.$collapsed ? "0" : "200px")};
  overflow: hidden;
  transition: width 0.3s ease;
`;

const Sider = styled.div<{ $collapsed: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: auto;
  max-height: 100vh;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  transition: transform 0.3s ease;
  overflow-y: auto;
  padding-top: 60px;
  width: 200px;
  transform: ${(props) => (props.$collapsed ? "translateX(-200px)" : "translateX(0)")};
  box-shadow: ${(props) => (props.$collapsed ? "none" : "2px 0 10px rgba(0, 0, 0, 0.5)")};
  @media (max-width: 768px) {
    width: 150px;
    padding-top: 50px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  max-width: 100vw;
  margin: 0;
  position: relative;
  align-items: flex-start;
  padding-top: 60px;
  flex: 1;
  -webkit-overflow-scrolling: touch;
  @media (max-width: 768px) {
    padding: 0;
    padding-top: 40px;
  }
`;

const ContentActivityLogWrapper = styled.div`
  width: 100%;
  padding: 20px 0;
  background: #ffe082;
`;

const TopRightContainer = styled.div`
  position: fixed;
  top: 40px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  z-index: 900;
  max-width: calc(100vw - 40px);
  @media (max-width: 768px) {
    top: 30px;
    right: 15px;
    gap: 4px;
    max-width: calc(100vw - 30px);
  }
`;

const ShortcutBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: rgba(255, 204, 0, 0.9);
  padding: 5px 10px;
  border: 2px solid #0427f4;
  border-radius: 8px;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.2);
  width: auto;
  height: auto;
  gap: 10px;
  @media (max-width: 768px) {
    padding: 3px 8px;
    gap: 5px;
  }
`;

const ShortcutLink = styled(Link)`
  color: #0427f4;
  font-size: 14px;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
    color: #1890ff;
  }
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const CalendarLinkWrapper = styled(Link)`
  display: inline-block;
  &:hover {
    text-decoration: none;
  }
`;

const LightTimeLinkWrapper = styled(Link)`
  display: inline-block;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`;

const TimerLinkWrapper = styled(Link)`
  display: inline-block;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`;

const TimerDisplay = styled.div<{ $isSessionActive: boolean }>`
  display: flex;
  align-items: center;
  background: rgba(97, 13, 127, 0.9);
  padding: 5px 10px;
  border: 2px solid #0427f4;
  border-radius: 8px;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.2);
  width: auto;
  height: auto;
  position: relative;
  @media (max-width: 768px) {
    padding: 3px 8px;
  }
  ${(props) =>
    props.$isSessionActive &&
    `
      @keyframes blink {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
      }
      &::after {
        content: '';
        position: absolute;
        top: -8px;
        left: -8px;
        right: -8px;
        bottom: -8px;
        border: 4px solid #00ff00;
        border-radius: 12px;
        animation: blink 1s infinite;
        z-index: -1;
      }
    `}
`;

const TimerText = styled.div`
  color: rgb(251, 251, 249);
  font-size: 14px;
  font-weight: bold;
  white-space: nowrap;
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const PublicLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(true);
  const location = useLocation();
  const { timer, isSessionActive, sessionProgress, intervalLabel } = useOlsmeTimer();
  const hasRenderedFooter = useRef(false);
  const prevTimerRef = useRef(timer);
  const renderCount = useRef(0);
  const isMounted = useRef(false);

  const toggleCollapsedMenu = useCallback(() => {
    setCollapsed((prev) => !prev);
    console.log("Verification: Collapsed state toggled to:", !collapsed);
  }, [collapsed]);

  useEffect(() => {
    if (isMounted.current) {
      renderCount.current += 1;
      console.log('Verification: PublicLayout render count:', renderCount.current, 'for path:', location.pathname);
      setCollapsed(true);
      hasRenderedFooter.current = false;
    } else {
      isMounted.current = true;
    }
    return () => {
      console.log("Verification: PublicLayout cleanup for path:", location.pathname);
      hasRenderedFooter.current = false;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (prevTimerRef.current !== timer) {
      prevTimerRef.current = timer;
      console.log("Verification: Timer updated:", timer, "isSessionActive:", isSessionActive, "progress:", sessionProgress);
    }
  }, [timer, isSessionActive, sessionProgress]);

  const shouldRenderActivityLog = !["/referral"].includes(location.pathname);

  const timerProps = React.useMemo(() => ({
    progress: sessionProgress,
    intervalLabel,
    isSessionActive,
  }), [sessionProgress, intervalLabel, isSessionActive]);

  return (
    <>
      <Helmet>
        <title>OneLightSystem | Sun Light Meditation Education</title>
        <meta name="description" content="Join OneLightSystem (OLS) at olsme.com/OLSStudentProspect for Sun Light Meditation education and a radiant Light Civilization." />
        <meta name="keywords" content="Sun Light Meditation, OLS meditation education, Light Civilization, OLSStudentProspect" />
        <meta property="og:title" content="OneLightSystem - Sun Light Meditation" />
        <meta property="og:description" content="Explore OLS’s meditation education at olsme.com/OLSStudentProspect." />
        <meta property="og:url" content="https://olsme.com/OLSStudentProspect" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <ErrorBoundary>
        <GlobalStyle />
        <LayoutWrapper>
          <TopRightContainer>
            <ShortcutBar>
              <ShortcutLink to="/login" aria-label="Go to Login page">Login</ShortcutLink>
              <ShortcutLink to="/register" aria-label="Go to Register page">Register</ShortcutLink>
            </ShortcutBar>
            <CalendarLinkWrapper to="/OLScalendarTime" aria-label="View OLS Calendar Time">
              <Tooltip title="View OLS Calendar Time">
                <OLSCalendar />
              </Tooltip>
            </CalendarLinkWrapper>
            <LightTimeLinkWrapper to="/OLScalendarTime" aria-label="View Light Time">
              <Tooltip title="View Light Time">
                <MiniLightTimeToggle $isDh={true} />
              </Tooltip>
            </LightTimeLinkWrapper>
            <TimerLinkWrapper to="/olsme" aria-label="View Timer">
              <TimerDisplay $isSessionActive={isSessionActive}>
                <TimerText>{timer}</TimerText>
              </TimerDisplay>
            </TimerLinkWrapper>
            <TimerBar {...timerProps} />
          </TopRightContainer>

          <MobileMenuButton
            icon={<MenuOutlined />}
            onClick={toggleCollapsedMenu}
            aria-label={collapsed ? "Open menu" : "Close menu"}
          />

          <SiderContainer $collapsed={collapsed}>
            <Sider $collapsed={collapsed}>
              <MenuLogin toggleCollapsed={toggleCollapsedMenu} collapsed={collapsed} />
            </Sider>
          </SiderContainer>

          <Content>
            {children || <Outlet />}
            {shouldRenderActivityLog && (
              <ContentActivityLogWrapper>
                <ActivityLog />
              </ContentActivityLogWrapper>
            )}
          </Content>

          <Footer />
          <ChatPa /> {/* Positioned above other elements with higher z-index */}
        </LayoutWrapper>
      </ErrorBoundary>
    </>
  );
};

export default memo(PublicLayout);